// LT-148 — Settings → Pipelines. Pipeline list + stage editor (add, rename,
// drag-reorder, remove), terminal-stage protection.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Check,
  GripVertical,
  Layers,
  Plus,
  Star,
  Trash2,
} from 'lucide-react';

import { pipelinesAPI } from '../../../services/pipelinesAPI';
import type {
  Pipeline,
  PipelineStage,
  TerminalType,
} from '../../../types/pipeline';

interface Toast {
  type: 'success' | 'error';
  text: string;
}

function extractMessage(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { detail?: unknown } } };
  const detail = e?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object' && 'message' in (detail as Record<string, unknown>)) {
    return String((detail as Record<string, unknown>).message);
  }
  return fallback;
}

const PipelinesTab: React.FC = () => {
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<Toast | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [newPipelineName, setNewPipelineName] = useState('');
  const [renamingPipelineId, setRenamingPipelineId] = useState<string | null>(
    null
  );
  const [renamePipelineValue, setRenamePipelineValue] = useState('');
  const [newStageName, setNewStageName] = useState('');
  const [newStageTerminal, setNewStageTerminal] = useState<TerminalType>(null);
  const [renamingStageId, setRenamingStageId] = useState<string | null>(null);
  const [renameStageValue, setRenameStageValue] = useState('');
  const [dragStageId, setDragStageId] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const list = await pipelinesAPI.list();
      setPipelines(list);
      if (list.length > 0 && (selectedId === null || !list.find((p) => p.id === selectedId))) {
        const def = list.find((p) => p.is_default) ?? list[0];
        setSelectedId(def.id);
      }
    } catch (err) {
      setToast({ type: 'error', text: extractMessage(err, 'Failed to load pipelines') });
    } finally {
      setLoading(false);
    }
  }, [selectedId]);

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selected = pipelines.find((p) => p.id === selectedId) ?? null;

  const handleCreatePipeline = async () => {
    if (!newPipelineName.trim()) return;
    try {
      const p = await pipelinesAPI.create({
        name: newPipelineName.trim(),
        is_default: pipelines.length === 0,
      });
      setNewPipelineName('');
      setSelectedId(p.id);
      await reload();
      setToast({ type: 'success', text: `Created pipeline "${p.name}"` });
    } catch (err) {
      setToast({ type: 'error', text: extractMessage(err, 'Could not create pipeline') });
    }
  };

  const handleRenamePipeline = async (id: string) => {
    if (!renamePipelineValue.trim()) return;
    try {
      await pipelinesAPI.update(id, { name: renamePipelineValue.trim() });
      setRenamingPipelineId(null);
      setRenamePipelineValue('');
      await reload();
    } catch (err) {
      setToast({ type: 'error', text: extractMessage(err, 'Could not rename pipeline') });
    }
  };

  const handleMarkDefault = async (id: string) => {
    try {
      await pipelinesAPI.update(id, { is_default: true });
      await reload();
      setToast({ type: 'success', text: 'Default pipeline updated' });
    } catch (err) {
      setToast({ type: 'error', text: extractMessage(err, 'Could not mark default') });
    }
  };

  const handleDeletePipeline = async (id: string) => {
    if (!window.confirm('Delete this pipeline? It must have no opportunities.')) return;
    try {
      await pipelinesAPI.remove(id);
      if (selectedId === id) setSelectedId(null);
      await reload();
      setToast({ type: 'success', text: 'Pipeline deleted' });
    } catch (err) {
      setToast({ type: 'error', text: extractMessage(err, 'Could not delete pipeline') });
    }
  };

  const handleAddStage = async () => {
    if (!selected || !newStageName.trim()) return;
    try {
      await pipelinesAPI.addStage(selected.id, {
        name: newStageName.trim(),
        is_terminal: newStageTerminal !== null,
        terminal_type: newStageTerminal,
      });
      setNewStageName('');
      setNewStageTerminal(null);
      await reload();
    } catch (err) {
      setToast({ type: 'error', text: extractMessage(err, 'Could not add stage') });
    }
  };

  const handleRenameStage = async (stageId: string) => {
    if (!selected || !renameStageValue.trim()) return;
    try {
      await pipelinesAPI.renameStage(selected.id, stageId, {
        name: renameStageValue.trim(),
      });
      setRenamingStageId(null);
      setRenameStageValue('');
      await reload();
    } catch (err) {
      setToast({ type: 'error', text: extractMessage(err, 'Could not rename stage') });
    }
  };

  const handleRemoveStage = async (stageId: string) => {
    if (!selected) return;
    if (!window.confirm('Remove this stage?')) return;
    try {
      await pipelinesAPI.removeStage(selected.id, stageId);
      await reload();
      setToast({ type: 'success', text: 'Stage removed' });
    } catch (err) {
      setToast({ type: 'error', text: extractMessage(err, 'Could not remove stage') });
    }
  };

  const onDragStart = (stageId: string) => (e: React.DragEvent) => {
    setDragStageId(stageId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', stageId);
  };

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const onDropBefore = (targetStageId: string) => async (e: React.DragEvent) => {
    e.preventDefault();
    if (!selected || !dragStageId || dragStageId === targetStageId) {
      setDragStageId(null);
      return;
    }
    const stages = [...selected.stages];
    const fromIdx = stages.findIndex((s) => s.id === dragStageId);
    const toIdx = stages.findIndex((s) => s.id === targetStageId);
    if (fromIdx < 0 || toIdx < 0) {
      setDragStageId(null);
      return;
    }
    const [moved] = stages.splice(fromIdx, 1);
    stages.splice(toIdx, 0, moved);
    setDragStageId(null);
    try {
      await pipelinesAPI.reorderStages(
        selected.id,
        stages.map((s) => s.id)
      );
      await reload();
    } catch (err) {
      setToast({ type: 'error', text: extractMessage(err, 'Could not reorder stages') });
    }
  };

  const terminalChipClass = (t: TerminalType) => {
    if (t === 'won') return 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40';
    if (t === 'lost') return 'bg-rose-500/20 text-rose-300 border border-rose-500/40';
    return 'bg-slate-500/20 text-slate-300 border border-slate-500/40';
  };

  const isLastTerminalOfKind = (stage: PipelineStage): boolean => {
    if (!selected || !stage.is_terminal || !stage.terminal_type) return false;
    return (
      selected.stages.filter(
        (s) => s.is_terminal && s.terminal_type === stage.terminal_type
      ).length === 1
    );
  };

  return (
    <div className="space-y-4">
      {toast && (
        <div
          className={`px-3 py-2 rounded-lg text-sm flex items-center justify-between ${
            toast.type === 'success'
              ? 'bg-green-500/20 border border-green-400/40 text-green-100'
              : 'bg-red-500/20 border border-red-400/40 text-red-100'
          }`}
        >
          <span>{toast.text}</span>
          <button
            onClick={() => setToast(null)}
            className="ml-2 text-gray-400 hover:text-white text-lg leading-none"
          >
            &times;
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Pipeline list */}
        <div className="md:col-span-1 bg-white/5 rounded-lg p-3">
          <h3 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
            <Layers className="w-4 h-4" /> Pipelines
          </h3>

          {loading ? (
            <p className="text-xs text-gray-400">Loading…</p>
          ) : pipelines.length === 0 ? (
            <p className="text-xs text-gray-400">No pipelines yet.</p>
          ) : (
            <ul className="space-y-1">
              {pipelines.map((p) => (
                <li
                  key={p.id}
                  className={`rounded-md px-2 py-1.5 text-sm cursor-pointer flex items-center justify-between gap-2 ${
                    p.id === selectedId
                      ? 'bg-cyan-500/20 text-cyan-100'
                      : 'text-gray-300 hover:bg-white/5'
                  }`}
                  onClick={() => setSelectedId(p.id)}
                >
                  {renamingPipelineId === p.id ? (
                    <input
                      autoFocus
                      value={renamePipelineValue}
                      onChange={(e) => setRenamePipelineValue(e.target.value)}
                      onBlur={() => handleRenamePipeline(p.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleRenamePipeline(p.id);
                        if (e.key === 'Escape') setRenamingPipelineId(null);
                      }}
                      className="flex-1 bg-slate-800 border border-slate-600 rounded px-2 py-0.5 text-white text-xs"
                    />
                  ) : (
                    <span
                      className="truncate flex-1"
                      onDoubleClick={(e) => {
                        e.stopPropagation();
                        setRenamingPipelineId(p.id);
                        setRenamePipelineValue(p.name);
                      }}
                    >
                      {p.name}
                    </span>
                  )}
                  {p.is_default && (
                    <Star className="w-3.5 h-3.5 text-yellow-300" aria-label="default" />
                  )}
                  <div className="flex items-center gap-1">
                    {!p.is_default && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          void handleMarkDefault(p.id);
                        }}
                        className="text-xs text-gray-400 hover:text-yellow-300"
                        title="Mark as default"
                      >
                        <Star className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        void handleDeletePipeline(p.id);
                      }}
                      className="text-gray-400 hover:text-rose-400"
                      title="Delete pipeline"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <div className="mt-3 flex gap-2">
            <input
              placeholder="New pipeline name"
              value={newPipelineName}
              onChange={(e) => setNewPipelineName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void handleCreatePipeline();
              }}
              className="flex-1 bg-white/5 border border-white/10 rounded px-2 py-1 text-white text-xs"
            />
            <button
              onClick={() => void handleCreatePipeline()}
              className="bg-cyan-600 hover:bg-cyan-700 px-2 py-1 rounded text-white text-xs flex items-center gap-1"
            >
              <Plus className="w-3.5 h-3.5" /> Add
            </button>
          </div>
        </div>

        {/* Stage editor */}
        <div className="md:col-span-2 bg-white/5 rounded-lg p-3">
          {!selected ? (
            <p className="text-sm text-gray-400">Select a pipeline to edit its stages.</p>
          ) : (
            <>
              <h3 className="text-sm font-semibold text-white mb-2 flex items-center justify-between">
                <span>Stages — {selected.name}</span>
                {selected.is_default && (
                  <span className="text-xs text-yellow-300 flex items-center gap-1">
                    <Star className="w-3.5 h-3.5" /> default
                  </span>
                )}
              </h3>

              <ul className="space-y-1">
                {selected.stages.map((stage) => (
                  <li
                    key={stage.id}
                    draggable
                    onDragStart={onDragStart(stage.id)}
                    onDragOver={onDragOver}
                    onDrop={onDropBefore(stage.id)}
                    className="flex items-center gap-2 bg-slate-900/40 rounded px-2 py-1.5"
                  >
                    <GripVertical className="w-3.5 h-3.5 text-gray-400 cursor-move" />
                    <span className="text-xs text-gray-400 w-6 text-right">#{stage.position}</span>
                    {renamingStageId === stage.id ? (
                      <input
                        autoFocus
                        value={renameStageValue}
                        onChange={(e) => setRenameStageValue(e.target.value)}
                        onBlur={() => handleRenameStage(stage.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleRenameStage(stage.id);
                          if (e.key === 'Escape') setRenamingStageId(null);
                        }}
                        className="flex-1 bg-slate-800 border border-slate-600 rounded px-2 py-0.5 text-white text-xs"
                      />
                    ) : (
                      <span
                        className="flex-1 text-sm text-white"
                        onDoubleClick={() => {
                          setRenamingStageId(stage.id);
                          setRenameStageValue(stage.name);
                        }}
                      >
                        {stage.name}
                      </span>
                    )}
                    {stage.is_terminal && (
                      <span
                        className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${terminalChipClass(stage.terminal_type)}`}
                      >
                        {stage.terminal_type}
                      </span>
                    )}
                    <button
                      disabled={isLastTerminalOfKind(stage)}
                      title={
                        isLastTerminalOfKind(stage)
                          ? `Cannot remove the last ${stage.terminal_type} stage`
                          : 'Remove stage'
                      }
                      onClick={() => void handleRemoveStage(stage.id)}
                      className={`text-gray-400 hover:text-rose-400 ${
                        isLastTerminalOfKind(stage) ? 'opacity-40 cursor-not-allowed hover:text-gray-400' : ''
                      }`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </li>
                ))}
              </ul>

              <div className="mt-3 flex flex-wrap gap-2 items-center">
                <input
                  placeholder="New stage name"
                  value={newStageName}
                  onChange={(e) => setNewStageName(e.target.value)}
                  className="flex-1 bg-white/5 border border-white/10 rounded px-2 py-1 text-white text-xs min-w-[140px]"
                />
                <select
                  value={newStageTerminal ?? ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    setNewStageTerminal(v === 'won' || v === 'lost' ? v : null);
                  }}
                  className="bg-white/5 border border-white/10 rounded px-2 py-1 text-white text-xs"
                >
                  <option value="">Non-terminal</option>
                  <option value="won">Terminal — won</option>
                  <option value="lost">Terminal — lost</option>
                </select>
                <button
                  onClick={() => void handleAddStage()}
                  className="bg-cyan-600 hover:bg-cyan-700 px-2 py-1 rounded text-white text-xs flex items-center gap-1"
                >
                  <Plus className="w-3.5 h-3.5" /> Add stage
                </button>
              </div>

              <p className="mt-2 text-[11px] text-gray-400 flex items-center gap-1">
                <Check className="w-3.5 h-3.5" />
                Double-click a stage or pipeline to rename. Drag the grip to reorder.
                Terminal <span className="text-emerald-400">won</span> /{' '}
                <span className="text-rose-400">lost</span> stages are protected from removal when last of their kind.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PipelinesTab;
